import React from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import TableRow from 'components/atoms/Tables/TableRow/TableRow';
import TableCell from 'components/atoms/Tables/TableCell/TableCell';
import {H6, P} from 'components/atoms/Text/Typography/Typography';
import Button from 'components/atoms/Buttons/Button/Button';
import Link from 'components/atoms/Links/Link/Link';
import StyledDailyEmailPage from 'components/pages/Dev/Email/DailyEmailPage/DailyEmailPage.styles';
import List from 'components/atoms/Lists/List/List';
import ListItem from 'components/atoms/Lists/ListItem/ListItem';
import {useClientComponent, useClientConfig} from 'components/organisms/Providers/ClientProvider/ClientProvider';
import ThemeProvider from 'components/organisms/Providers/ThemeProvider/ThemeProvider';
import utils from 'helpers/utils';
import constants from 'helpers/constants';

const path = utils.componentPath(import.meta.url);

const DailyEmailPage = (props) => {
  const {
    inherited,
    ...innerProps
  } = useComponentProps(props, 'DailyEmailPage');

  const clientConfig = useClientConfig(`${path}/${innerProps.$componentName}`);
  const Component = useClientComponent(`${path}/${innerProps.$componentName}`);

  const renderEmail = () => {
    if (Component) {
      return <Component {...props} config={clientConfig} />
    } else {
      return <StyledDailyEmailPage {...innerProps}
                                   config={clientConfig}
                                   preview={`There is new activity for you in ${constants.basics.shortName}. Check it out here.`}>
        <TableRow>
          <TableCell colSpan={2}>
            <P>There is new activity for you in {constants.basics.shortName}. Here is an overview of the current activity for you.</P>
          </TableCell>
        </TableRow>
        <TableRow className="DailyEmailPage-recentRow">
          <TableCell colSpan={2}>
            <H6>Recent activity:</H6>
            <List>
              <ListItem>You have {'{{UNREAD_COMMENTS}}'} unread comment(s)</ListItem>
              <ListItem>You have {'{{UNREAD_NOTIFICATIONS}}'} unread notification(s)</ListItem>
              <ListItem>You have {'{{DUE_TASKS}}'} due task(s)</ListItem>
            </List>
          </TableCell>
        </TableRow>
        <TableRow className="DailyEmailPage-commentsNotificationsTasksRow">
          <TableCell colSpan={2}>
            <H6>Comments, Notifications and Tasks:</H6>
            <List>
              <ListItem>You have {'{{UNREAD_COMMENTS_TOTAL}}'} unread comment(s) in total
                and {'{{UNREAD_COMMENTS_TEAM}}'} unread team comment(s)</ListItem>
              <ListItem>You have {'{{UNREAD_NOTIFICATIONS_TOTAL}}'} unread notification(s) in total
                and {'{{UNREAD_NOTIFICATIONS_TEAM}}'} unread team notification(s)</ListItem>
              <ListItem>You have {'{{OPEN_TASKS}}'} open task(s) in total and {'{{OPEN_TASKS_TEAM}}'} team
                task(s)</ListItem>
            </List>
          </TableCell>
        </TableRow>
        <TableRow className="DailyEmailPage-recentCommentsRow">
          <TableCell colSpan={2}>
            <H6>Most recent comment(s):</H6>
            <List>
              <ListItem>
                <P component="div">{'{{UNREAD_COMMENTS_ITEM_TITLE}}'}</P>
                <P component="div" color="text.secondary">{'{{UNREAD_COMMENTS_ITEM_TEXT}}'}</P>
              </ListItem>
            </List>
          </TableCell>
        </TableRow>
        <TableRow className="DailyEmailPage-recentNotificationsRow">
          <TableCell colSpan={2}>
            <H6>Most recent notification(s):</H6>
            <List>
              <ListItem>
                <P component="div">{'{{UNREAD_NOTIFICATIONS_ITEM_TITLE}}'}</P>
                <P component="div" color="text.secondary">{'{{UNREAD_NOTIFICATIONS_ITEM_TEXT}}'}</P>
              </ListItem>
            </List>
          </TableCell>
        </TableRow>
        <TableRow className="DailyEmailPage-dueTasksRow">
          <TableCell colSpan={2}>
            <H6>Due task(s):</H6>
            <List>
              <ListItem>
                <P component="div">{'{{DUE_TASKS_ITEM_TITLE}}'}</P>
                <P component="div" color="text.secondary">{'{{DUE_TASKS_ITEM_TEXT}}'}</P>
                <P component="div" color="text.secondary" className="due">{'{{DUE_TASKS_ITEM_DATE}}'}</P>
              </ListItem>
            </List>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2}>
            <P>You can find all this information easily on the {constants.basics.shortName} dashboard page. Use this link to go to the
              dashboard:</P>
          </TableCell>
        </TableRow>
        <TableRow className="DailyEmailPage-dashboardButtonRow">
          <TableCell colSpan={2}>
            <Button component="a" href="{{DASHBOARD_LINK}}" variant="contained" size="medium">Open dashboard</Button>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2}>
            <P>
              Or cut and paste this text if the link doesn't work:<br/>
              <Link className="system" href="{{DASHBOARD_LINK}}">{'{{DASHBOARD_LINK}}'}</Link>
            </P>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell colSpan={2}>
            <P>If all went well, you can now see your activity. Are you still unsure, or do you still have questions?
              Please let us know.</P>
          </TableCell>
        </TableRow>
      </StyledDailyEmailPage>
    }
  }

  if (clientConfig?.theme) {
    return <ThemeProvider name={clientConfig?.theme.name} overrides={clientConfig?.theme?.overrides}>
      {renderEmail()}
    </ThemeProvider>
  } else {
    return renderEmail();
  }
};

DailyEmailPage.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DailyEmailPage;
